import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { thumbUrl, posterUrl, api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Lock, Images, Loader2, Heart } from "lucide-react";
import { SmartCover, CoverAdjustButton } from "@/components/SmartCover";
import { useAuth } from "@/context/AuthContext";

const CollectionCard = ({ c }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cover = c.thumbs?.[0];
  const [frame, setFrame] = useState(cover?.frame);
  useEffect(() => { setFrame(cover?.frame); }, [cover?.frame]);
  const src = cover ? (cover.media_type === "video" ? (cover.poster || posterUrl(cover, 600)) : thumbUrl(cover.file_ref, 600)) : null;
  return (
    <div className="relative group">
      <button data-testid={`collection-card-${c.id}`} onClick={() => navigate(`/collection/${c.id}`)}
        className="w-full text-left rounded-xl overflow-hidden border border-border bg-card hover:border-primary/50 transition-all">
        <div className="aspect-[4/3] bg-black relative">
          {src ? <SmartCover src={src} frame={frame} aspect={4 / 3} /> : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Images className="w-8 h-8" /></div>
          )}
          {c.count > 1 && (
            <span className="absolute bottom-2 right-2 text-[11px] font-medium px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-white inline-flex items-center gap-1">
              <Images className="w-3 h-3" /> {c.count}
            </span>
          )}
          {c.likes > 0 && (
            <span className="absolute bottom-2 left-2 text-[11px] font-medium px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-rose-300 inline-flex items-center gap-1">
              <Heart className="w-3 h-3 fill-rose-400" /> {c.likes}
            </span>
          )}
        </div>
        <div className="p-3">
          <div className="flex items-center gap-1.5">
            {c.visibility === "private" && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
            <span className="font-medium truncate">{c.name}</span>
          </div>
          <span className="text-xs text-muted-foreground">{c.count} {c.count === 1 ? "item" : "items"}</span>
        </div>
      </button>
      {user?.is_admin && cover?.id && src && <CoverAdjustButton mediaId={cover.id} src={src} aspect={4 / 3} onSaved={setFrame} />}
    </div>
  );
};

export const CollectionsRow = ({ collections, editable, onCreated }) => {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try { const r = await api.post("/collections", { name }); toast.success("Gallery created"); setName(""); setCreating(false); onCreated?.(r.data); }
    catch { toast.error("Failed"); }
    setSaving(false);
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="collections-row">
      {(collections || []).map((c) => <CollectionCard key={c.id} c={c} />)}
      {editable && (
        creating ? (
          <div className="rounded-xl border border-dashed border-primary/50 bg-card p-4 flex flex-col justify-center gap-2">
            <input autoFocus data-testid="collection-new-name" value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && create()} placeholder="Gallery name"
              className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
            <div className="flex gap-2">
              <button data-testid="collection-create-confirm" onClick={create} disabled={saving} className="flex-1 rounded-full bg-primary text-primary-foreground text-sm py-1.5 font-medium">{saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Create"}</button>
              <button onClick={() => { setCreating(false); setName(""); }} className="rounded-full border border-border text-sm px-3">Cancel</button>
            </div>
          </div>
        ) : (
          <button data-testid="collection-new-btn" onClick={() => setCreating(true)}
            className="rounded-xl border border-dashed border-border hover:border-primary/50 bg-card/50 flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground hover:text-primary transition-colors min-h-[160px]">
            <Plus className="w-7 h-7" />
            <span className="text-sm font-medium">New gallery</span>
          </button>
        )
      )}
    </div>
  );
};
